/**
 * Demo Mode Utilities
 *
 * Demo mode allows the application to run with mock data for screenshot purposes.
 *
 * To enable demo mode:
 * 1. Set VITE_DEMO_MODE_ENABLED=true in your .env file (REQUIRED - safety check)
 * 2. Add ?demo=true to the URL
 *
 * Both conditions must be met for demo mode to activate.
 * This ensures demo mode can never accidentally be enabled in production.
 *
 * Once activated, demo mode persists in sessionStorage for the browser session.
 */

const DEMO_MODE_KEY = 'cortado_demo_mode'

/**
 * Check if demo mode is enabled via environment variable
 * This must be set at build time - acts as a safety gate
 */
export function isDemoModeAllowed(): boolean {
  return import.meta.env.VITE_DEMO_MODE_ENABLED === 'true'
}

/**
 * Check if demo mode is requested via URL parameter or stored in session
 */
export function isDemoModeRequested(): boolean {
  if (typeof window === 'undefined') return false

  // Check URL parameter first
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('demo') === 'true') {
    // Store in sessionStorage for persistence across navigations
    sessionStorage.setItem(DEMO_MODE_KEY, 'true')
    return true
  }

  // Fall back to sessionStorage
  return sessionStorage.getItem(DEMO_MODE_KEY) === 'true'
}

/**
 * Check if demo mode is active (both env flag AND url param/session must be true)
 */
export function isDemoModeActive(): boolean {
  return isDemoModeAllowed() && isDemoModeRequested()
}

/**
 * Clear demo mode from sessionStorage (useful for exiting demo mode)
 */
export function clearDemoMode(): void {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(DEMO_MODE_KEY)
  }
}

/**
 * Get the current demo mode status for debugging
 */
export function getDemoModeStatus(): {
  allowed: boolean
  requested: boolean
  active: boolean
} {
  return {
    allowed: isDemoModeAllowed(),
    requested: isDemoModeRequested(),
    active: isDemoModeActive(),
  }
}
